import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, NotYetImplemented, TypeTag } from '../../components/ui';
import { useTask } from './useTask';
import { numberSteps } from './taskGraph';

const LINK = 'underline text-ink font-semibold focus-ring';

function PublicTask({ id, task }) {
  const { template, graph, levels } = task;
  const title = template.template_name;
  const numbers = numberSteps(graph, levels);
  const byKey = Object.fromEntries(graph.steps.map((s) => [s.key, s]));
  const steps = levels.flat().map((k) => byKey[k]).filter((s) => s && !s.isCheck);
  const owner = null; // templates carry no owner yet (bjjg.15, bjjg.7)
  const based = `Based on ${owner ? `${owner}'s` : "a colleague's"} task`;

  return (
    <div className="bg-paper text-ink min-h-full">
      <div className="max-w-3xl mx-auto px-6 py-9 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <NotYetImplemented feature="orgPublic">
            <p className="text-sm text-pencil">Made public by {owner || 'a colleague'}</p>
          </NotYetImplemented>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">{title}</h1>
          {template.description ? <p className="text-pencil">{template.description}</p> : null}
        </header>

        <div className="flex items-center gap-3 flex-wrap">
          <Button asChild variant="primary">
            <Link to={`/tasks/${encodeURIComponent(id)}/run`}>Run</Link>
          </Button>
          <NotYetImplemented inline feature="orgPublic">
            <Button variant="secondary">Make my copy</Button>
          </NotYetImplemented>
        </div>

        <section aria-label="Steps">
          {steps.length === 0 ? (
            <p className="text-pencil">No steps yet.</p>
          ) : (
            <ol aria-label="Steps" className="flex flex-col gap-2 list-none p-0 m-0">
              {steps.map((s) => (
                <li key={s.key} className="flex items-center gap-3">
                  <span className="font-sans font-bold w-6 text-right">{numbers[s.key]}</span>
                  <span className="font-semibold">{s.title}</span>
                  <TypeTag type={s.type} />
                </li>
              ))}
            </ol>
          )}
        </section>

        <NotYetImplemented feature="orgPublic">
          <Card variant="static" className="p-4 flex flex-col gap-2">
            <div className="text-xs font-bold uppercase tracking-wider text-pencil">IN YOUR TASKS AFTERWARDS</div>
            <div className="font-sans font-bold">{title}</div>
            <div className="text-sm text-pencil">{based}</div>
            <div className="text-sm">{`${steps.length} ${steps.length === 1 ? 'step' : 'steps'} · Yours to change · Not public`}</div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="primary" size="sm">Run</Button>
              <Button variant="secondary" size="sm">Open</Button>
              <Button variant="secondary" size="sm">Copy to canvas</Button>
            </div>
          </Card>
        </NotYetImplemented>
      </div>
    </div>
  );
}

export default function PublicTaskPage() {
  const { id } = useParams();
  const task = useTask(id);
  if (task.loading) return <div className="p-9 text-pencil">Loading…</div>;
  if (task.error || !task.template) {
    return (
      <div className="p-9 flex items-center gap-3">
        <p role="alert" className="text-status-failed">Couldn't open this task.</p>
        <Link to="/tasks" className={LINK}>Tasks</Link>
      </div>
    );
  }
  return <PublicTask key={id} id={id} task={task} />;
}
